import React from 'react';
import Icon from 'components/AppIcon';

const TractionSlide = () => {
  const developmentStatus = [
    {
      category: 'Core Platform',
      status: 'completed',
      progress: 100,
      features: [
        'User authentication & security',
        'Responsive design system',
        'Database architecture',
        'API framework'
      ]
    },
    {
      category: 'Client Features',
      status: 'completed',
      progress: 100,
      features: [
        'Client registration portal',
        'Lawyer matching system',
        'Case progress tracking',
        'Document access portal'
      ]
    },
    {
      category: 'Lawyer Dashboard',
      status: 'completed',
      progress: 100,
      features: [
        'Complete case management',
        'Client management system',
        'Document management',
        'Financial tracking'
      ]
    },
    {
      category: 'Advanced Features',
      status: 'completed',
      progress: 100,
      features: [
        'Video consultation platform',
        'Partner portal with analytics',
        'Notification system',
        'Mobile optimization'
      ]
    }
  ];

  const technicalSpecs = [
    {
      icon: 'Code',
      title: 'Modern Tech Stack',
      details: ['React 18', 'Tailwind CSS', 'Node.js Backend', 'PostgreSQL Database'],
      status: 'Implemented'
    },
    {
      icon: 'Shield',
      title: 'Security Features',
      details: ['End-to-end encryption', 'Two-factor authentication', 'Data privacy compliance', 'Secure file storage'],
      status: 'Implemented'
    },
    {
      icon: 'Smartphone',
      title: 'Mobile Experience',
      details: ['Responsive design', 'PWA capabilities', 'Offline functionality', 'Touch-optimized UI'],
      status: 'Implemented'
    },
    {
      icon: 'Zap',
      title: 'Performance',
      details: ['Fast loading times', 'Optimized queries', 'CDN integration', 'Caching strategies'],
      status: 'Optimized'
    }
  ];

  const readinessMetrics = [
    {
      metric: 'Platform Stability',
      value: '99.9%',
      description: 'Uptime in testing environment',
      icon: 'Activity'
    },
    {
      metric: 'Feature Completion',
      value: '100%',
      description: 'MVP features fully implemented',
      icon: 'CheckCircle'
    },
    {
      metric: 'Security Score',
      value: 'A+',
      description: 'Security audit rating',
      icon: 'Shield'
    },
    {
      metric: 'Performance Score',
      value: '95/100',
      description: 'Google Lighthouse score',
      icon: 'Gauge'
    }
  ];

  const nextSteps = [
    {
      phase: 'Beta Testing',
      duration: '2 months',
      description: 'Limited release to 50 law firms for feedback and optimization',
      status: 'ready'
    },
    {
      phase: 'Public Launch',
      duration: '1 month',
      description: 'Full platform launch with marketing campaign',
      status: 'planned'
    },
    {
      phase: 'Scale & Expand',
      duration: '6 months',
      description: 'Customer acquisition and feature enhancements',
      status: 'planned'
    }
  ];

  const getStatusColor = (status) => {
    const colorMap = {
      'completed': 'text-success-600 bg-success-50',
      'in-progress': 'text-warning-600 bg-warning-50',
      'planned': 'text-primary bg-primary-50',
      'ready': 'text-success-600 bg-success-50'
    };
    return colorMap[status] || colorMap['planned'];
  };

  return (
    <div className="py-12">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-heading font-bold text-text-primary mb-4">
            Development Status & Traction
          </h1>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            Platform is fully developed and ready for market launch
          </p>
        </div>

        {/* Development Progress */}
        <div className="mb-12">
          <h2 className="text-2xl font-heading font-bold text-text-primary mb-8 text-center">
            Platform Development Status
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {developmentStatus?.map((item, index) => (
              <div key={index} className="bg-surface rounded-lg p-6 shadow-card border border-border">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-heading font-semibold text-text-primary">
                    {item?.category}
                  </h3>
                  <div className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(item?.status)}`}>
                    {item?.status === 'completed' ? 'Completed' : 'In Progress'}
                  </div>
                </div>
                
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-text-secondary">Progress</span>
                    <span className="text-sm font-medium text-text-primary">{item?.progress}%</span>
                  </div>
                  <div className="w-full bg-secondary-200 rounded-full h-2">
                    <div 
                      className="bg-success-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${item?.progress}%` }}
                    ></div>
                  </div>
                </div>
                
                <ul className="space-y-2">
                  {item?.features?.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center space-x-2 text-sm text-text-secondary">
                      <Icon name="Check" size={14} className="text-success-600 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Technical Specifications */}
        <div className="mb-12">
          <h2 className="text-2xl font-heading font-bold text-text-primary mb-8 text-center">
            Technical Implementation
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {technicalSpecs?.map((spec, index) => (
              <div key={index} className="bg-surface rounded-lg p-6 shadow-card border border-border">
                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mb-4">
                  <Icon name={spec?.icon} size={24} className="text-primary" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-text-primary mb-2">
                  {spec?.title}
                </h3>
                <ul className="space-y-1 mb-4">
                  {spec?.details?.map((detail, detailIndex) => (
                    <li key={detailIndex} className="text-sm text-text-secondary flex items-center space-x-1">
                      <Icon name="Dot" size={12} className="text-primary" />
                      <span>{detail}</span>
                    </li>
                  ))}
                </ul>
                <div className="bg-success-50 text-success-600 px-3 py-1 rounded-full text-xs font-medium text-center">
                  {spec?.status}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Readiness Metrics */}
        <div className="mb-12">
          <h2 className="text-2xl font-heading font-bold text-text-primary mb-8 text-center">
            Platform Readiness Metrics
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {readinessMetrics?.map((metric, index) => (
              <div key={index} className="bg-gradient-to-br from-primary-50 to-secondary-50 rounded-lg p-6 text-center">
                <div className="w-16 h-16 bg-primary rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Icon name={metric?.icon} size={32} color="white" />
                </div>
                <div className="text-3xl font-heading font-bold text-primary mb-2">
                  {metric?.value}
                </div>
                <h3 className="font-heading font-semibold text-text-primary mb-1">
                  {metric?.metric}
                </h3>
                <p className="text-sm text-text-secondary">
                  {metric?.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Next Steps */}
        <div className="bg-surface rounded-lg p-8 shadow-card border border-border">
          <h2 className="text-2xl font-heading font-bold text-text-primary mb-8 text-center">
            Go-to-Market Timeline
          </h2>
          <div className="space-y-6">
            {nextSteps?.map((step, index) => (
              <div key={index} className="flex items-center space-x-6">
                <div className="flex-shrink-0">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    step?.status === 'ready' ? 'bg-success-500' : 
                    step?.status === 'in-progress' ? 'bg-warning-500' : 'bg-primary'
                  }`}>
                    <span className="text-white font-bold">{index + 1}</span>
                  </div>
                </div>
                
                <div className="flex-1 bg-secondary-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-heading font-semibold text-text-primary">
                      {step?.phase}
                    </h3>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-text-secondary">{step?.duration}</span>
                      <div className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(step?.status)}`}>
                        {step?.status === 'ready' ? 'Ready' : step?.status === 'in-progress' ? 'In Progress' : 'Planned'}
                      </div>
                    </div>
                  </div>
                  <p className="text-text-secondary">
                    {step?.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TractionSlide;